import {Resizable} from 're-resizable'
import * as React from 'react'
import Draggable from 'react-draggable'
import {v4 as uuidv4} from 'uuid'
import {IPopupSettings} from '../../interfaces/models'
import {IExportElement, IExportMap} from '../../interfaces/reducers'
import ExportLegend from './ExportLegend'
import {ManagePrintText} from './ManagePrintText'

export interface Props {
  updateElementProps: (key: string, elementProps?: IExportElement) => void
  exportMapProps: IExportMap
  onTemplateRendered: (template: HTMLDivElement) => void
  theRealThing: boolean //means if its only for display or is really rendered into png/pdf :)
  addPopups: (popupSettings: {[id: string]: IPopupSettings}) => void
  removePopups: (popups: string[]) => void
}

export class DefaultTemplate extends React.Component<Props, {}> {
  private draggableRefs: {[key: string]: React.RefObject<HTMLDivElement>} = {}

  private getDraggableRef(key: string): React.RefObject<HTMLDivElement> {
    if (!this.draggableRefs[key]) {
      this.draggableRefs[key] = React.createRef<HTMLDivElement>()
    }
    return this.draggableRefs[key]
  }

  mmToPixels = (mms: number): string => {
    const unitMmFactor = this.props.theRealThing
      ? this.props.exportMapProps.unitMmFactor
      : this.props.exportMapProps.unitMmFactor * this.props.exportMapProps.dpiFactor
    return `${(this.props.exportMapProps.pixelsToUnitsFactor / unitMmFactor) * mms}px`
  }

  getTemplateScale(): JSX.Element {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          width: '100%',
          height: '100%',
          fontSize: this.mmToPixels(4)
        }}
      >
        {`M 1: ${this.props.exportMapProps.mapProps.scale.toFixed(0)}`}
      </div>
    )
  }

  getLegend(): JSX.Element {
    return <ExportLegend theRealThing={this.props.theRealThing} mmToPixels={this.mmToPixels} />
  }

  getTextElement(key: string, element: IExportElement): JSX.Element {
    let textElementScaled = element.text || ''
    if (this.props.theRealThing && textElementScaled) {
      const myRegexp = /font-size: (\d+)px;/g
      let match = myRegexp.exec(textElementScaled)
      while (match != null) {
        let matchCopy = match[0]
        matchCopy = matchCopy.replace(match[1], (parseInt(match[1]) * this.props.exportMapProps.dpiFactor).toFixed(0))
        textElementScaled = textElementScaled.replace(match[0], matchCopy)
        match = myRegexp.exec(textElementScaled)
      }
    }

    return (
      <div
        className="print-template"
        style={{fontSize: this.mmToPixels(element.textHeightMM!)}}
        dangerouslySetInnerHTML={{__html: textElementScaled}}
      ></div>
    )
  }

  createDraggableResizibleElement(key: string, element: JSX.Element, elementProps: IExportElement): JSX.Element {
    const dpiFactor = this.props.theRealThing ? this.props.exportMapProps.dpiFactor : 1
    const width = `${elementProps.resizibleWidth * dpiFactor}px`
    const height = `${elementProps.resizibleHeight * dpiFactor}px`
    const position = {
      x: elementProps.resizibleLeft * dpiFactor,
      y: elementProps.resizibleTop * dpiFactor
    }
    const bounds = {
      left: 0,
      right: this.props.theRealThing
        ? this.props.exportMapProps.defaultLayoutWidth
        : this.props.exportMapProps.defaultLayoutWidth / this.props.exportMapProps.dpiFactor,
      top: 0,
      bottom: this.props.theRealThing
        ? this.props.exportMapProps.defaultLayoutHeight
        : this.props.exportMapProps.defaultLayoutHeight / this.props.exportMapProps.dpiFactor
    }
    return (
      <Draggable
        axis={'both'}
        key={key}
        handle=".print-tool-draggable"
        position={position}
        bounds={bounds}
        nodeRef={this.getDraggableRef(key)}
        onStop={(e, d) => {
          this.props.updateElementProps(key, {...elementProps, resizibleLeft: d.lastX, resizibleTop: d.lastY})
        }}
      >
        <div ref={this.getDraggableRef(key)}>
          {this.props.theRealThing ? null : (
            <>
              <div className="print-tool print-tool-draggable" style={{cursor: 'move'}}>
                <i className="fas fa-arrows-alt" />
              </div>
              {elementProps.type == 'text' ? (
                <div
                  onClick={() => {
                    this.props.addPopups({
                      editExportText: {
                        content: (
                          <ManagePrintText
                            onOk={el => {
                              this.props.updateElementProps(key, el)
                              this.props.removePopups(['editExportText'])
                            }}
                            element={elementProps}
                          ></ManagePrintText>
                        ),
                        header: <div></div>,
                        width: '80%'
                      }
                    })
                  }}
                  className="print-tool"
                  style={{top: 40}}
                >
                  <i className="fas fa-edit" />
                </div>
              ) : null}
              {elementProps.type == 'text' ? (
                <div onClick={() => this.props.updateElementProps(key)} className="print-tool" style={{top: 80}}>
                  <i className="fas fa-trash" />
                </div>
              ) : null}
            </>
          )}
          <Resizable
            handleComponent={
              this.props.theRealThing
                ? undefined
                : {
                    bottomRight: <span className="resizible-teo-drag"></span>
                  }
            }
            key={'map'}
            onResize={(event, direction, elementRef, delta) => {
              if (elementRef.style.width && elementRef.style.height) {
                this.props.updateElementProps(key, {
                  ...elementProps,
                  resizibleWidth: parseInt(elementRef.style.width.replace('px', '')),
                  resizibleHeight: parseInt(elementRef.style.height.replace('px', ''))
                })
              }
            }}
            style={{overflow: 'hidden'}}
            size={{width: width, height: height}}
            maxWidth={bounds.right || '100%'}
            maxHeight={bounds.bottom || '100%'}
            enable={{
              right: true,
              bottom: true,
              bottomRight: true
            }}
          >
            {element}
          </Resizable>
        </div>
      </Draggable>
    )
  }

  getElements(): JSX.Element[] {
    const arr: JSX.Element[] = []

    for (const key in this.props.exportMapProps.elements) {
      const element = this.props.exportMapProps.elements[key]
      if (!element.hide) {
        if (element.type == 'legend') {
          arr.push(this.createDraggableResizibleElement(key, this.getLegend(), element))
        }
        if (element.type == 'scale') {
          arr.push(this.createDraggableResizibleElement(key, this.getTemplateScale(), element))
        }
        if (element.type == 'text') {
          arr.push(this.createDraggableResizibleElement(key, this.getTextElement(key, element), element))
        }
      }
    }

    return arr
  }
  getPosition(el: HTMLElement) {
    let xPosition = 0
    let yPosition = 0

    while (el) {
      if (el.tagName == 'BODY') {
        // deal with browser quirks with body/window/document and page scroll
        const xScrollPos = el.scrollLeft || document.documentElement.scrollLeft
        const yScrollPos = el.scrollTop || document.documentElement.scrollTop

        xPosition += el.offsetLeft - xScrollPos + el.clientLeft
        yPosition += el.offsetTop - yScrollPos + el.clientTop
      } else {
        xPosition += el.offsetLeft - el.scrollLeft + el.clientLeft
        yPosition += el.offsetTop - el.scrollTop + el.clientTop
      }

      el = el.offsetParent as HTMLElement
    }
    return {
      x: xPosition,
      y: yPosition
    }
  }
  getTemplate(): JSX.Element {
    const width = this.props.theRealThing
      ? this.props.exportMapProps.defaultLayoutWidth
      : this.props.exportMapProps.defaultLayoutWidth / this.props.exportMapProps.dpiFactor
    const height = this.props.theRealThing
      ? this.props.exportMapProps.defaultLayoutHeight
      : this.props.exportMapProps.defaultLayoutHeight / this.props.exportMapProps.dpiFactor
    return (
      <div
        onDoubleClick={e => {
          if (!this.props.theRealThing) {
            const targetPos = this.getPosition(e.target as HTMLElement)
            const newTextElement: IExportElement = {
              resizibleHeight: 200,
              resizibleWidth: 200,
              resizibleLeft: (e.pageX - targetPos.x) / this.props.exportMapProps.zoomFactor,
              resizibleTop: (e.pageY - targetPos.y) / this.props.exportMapProps.zoomFactor,
              type: 'text',
              text: 'Редактирай ме',
              textHeightMM: 3
            }
            this.props.addPopups({
              addExportText: {
                content: (
                  <ManagePrintText
                    onOk={el => {
                      this.props.updateElementProps(uuidv4(), el)
                      this.props.removePopups(['addExportText'])
                    }}
                    element={newTextElement}
                  ></ManagePrintText>
                ),
                header: <div></div>,
                width: '500px'
              }
            })
          }
        }}
        ref={e => this.props.onTemplateRendered(e!)}
        style={{
          margin: '0px',
          padding: '0px',
          border: '1px solid gray',
          position: this.props.theRealThing ? 'relative' : 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          boxSizing: 'border-box',
          minWidth: `${width}px`,
          minHeight: `${height}px`,
          maxWidth: `${width}px`,
          maxHeight: `${height}px`,
          width: `${width}px`,
          height: `${height}px`
        }}
      >
        {this.getElements()}
      </div>
    )
  }

  render() {
    return this.getTemplate()
  }
}
